import * as logger from "../utils/logger";
import { handleApi } from "../utils/error";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { Dialog } from "../ui/framework/dialog";
import { ITrailerAttachment, TrailerAttachment } from "../model/TrailerAttachment";
import * as common from "./common";
import { getCurrentVehicle } from "../utils/device";
import { BaseCache, makeTrailerCache, makeTrailerDeviceCache } from "../utils/storage/cache";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { isEqual, extend } from "../utils/vanilla";
import { syncDefectLists } from "./misc";
import { IModifiedType } from "./sync.types";
import { FeedTypeKey } from "./common";
import { getKeys } from "../utils/helpers";
import { TrailerAttachmentActions } from "../store/trailerAttachments/actions";
import { isDuplicateException, isOverLimitException, isRelationViolatedException } from "../utils/errorHandler/error.types";
import { isEntityRemoved, isOverlappingTrailerAttachmentException } from "../utils/errorHandler/error.messages";
import BaseSync, { ISyncAddSet, ISyncOptions, NumberOfSyncRetries } from "./base";
import { ITrailer, Trailer } from "../model/Trailer";
import { getServerDate } from "../utils/storage/date";
import { FeatureFlagName } from "../utils/featureFlagName";
import { addAsset, getAsset, getTrailerGroups } from "./deviceApiHelpers";
import { Device } from "../model/Device";
import { IApiError } from "../utils/api.interfaces";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";
import {
    primeTrailerDeviceIdentity, toDeviceLinkEntity, toTrailerAttachmentRead, usesTrailerDeviceIdentity
} from "./trailerAttachmentDeviceLink";
import { TrailerIdentityReconciler } from "./trailerIdentityReconciler";

export default class TrailerAttachmentSync extends BaseSync {
    private readonly trailerCache: BaseCache<ITrailer>;
    private readonly trailerDeviceCache: BaseCache<Device | IDevice>;
    private readonly trailerIdentityReconciler: TrailerIdentityReconciler;

    constructor(options: ISyncOptions) {
        super(options);
        // MYG-148762: see the note in DvirSync's constructor.
        primeTrailerDeviceIdentity(this.store);
        this.trailerCache = makeTrailerCache(this.store);
        this.trailerDeviceCache = makeTrailerDeviceCache(this.store);
        this.trailerIdentityReconciler = new TrailerIdentityReconciler(this.api, this.store);
    }

    defaults(): { name: string } {
        return {
            name: "Sync TrailerAttachment"
        };
    }

    getAttachments(): IIdDictionary<TrailerAttachment> {
        if (!this.currentUser) {
            return {};
        }

        const storedTrailerAttachments = this.store.getState().trailerAttachments[this.currentUser.id];
        const attachments: IIdDictionary<TrailerAttachment> = {};
        if (storedTrailerAttachments) {
            for (const id of getKeys(storedTrailerAttachments)) {
                const entity = extend(true, {}, storedTrailerAttachments[id]) as ITrailerAttachment;
                attachments[id] = new TrailerAttachment(entity);
            }
        }
        return attachments;
    }

    setAttachments(attachmentsToSave: IIdDictionary<TrailerAttachment>): void {
        this.currentUser && this.store.dispatch(TrailerAttachmentActions.setTrailerAttachments({ userId: this.currentUser.id, trailerAttachments: attachmentsToSave }));
    }

    clear(): void {
        const currentVehicle = getCurrentVehicle();
        const attachments = this.getAttachments();
        if (currentVehicle) {
            for (const key in attachments) {
                if (attachments[key].device.id !== currentVehicle.id) {
                    delete attachments[key];
                }
            }
        }
        this.setAttachments(attachments);
    }

    processOfflineAttachment(
        attachment: TrailerAttachment,
        tempTrailer: Trailer,
        currentActiveUserId?: string
    ): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const currentApiUser = this.currentUser;
            const getTrailerDeviceEntity = (name: string, comment: string): Device => {
                return new Device({
                    comment,
                    name,
                    groups: [{ "id": "GroupTrailerId" }],
                    activeFrom: getServerDate().toISOString(),
                    serialNumber: "",
                    timezoneId: currentApiUser?.timeZoneId
                });
            };
            const updateTrailerAttachment = (trailer: Trailer): void => {
                this.trailerCache.remove(tempTrailer, currentApiUser?.id);
                delete trailer._isTemp;
                this.trailerCache.add(trailer, currentApiUser?.id);
                attachment.trailer.id = trailer.id;
                resolve();
            };
            const searchParams = {
                name: tempTrailer.name,
                groups: [{ id: "GroupTrailerId" }],
                fromDate: getServerDate().toISOString()
            };
            return getAsset(this.api, searchParams, "Device")
                .then(result => {
                    if (result && result.length > 0) {
                        updateTrailerAttachment(this.trailerFromDevice(result[0]));
                        return;
                    }
                    // Skip add for non-active users to avoid creating duplicate trailers and resolve
                    if (currentApiUser?.id !== currentActiveUserId) {
                        resolve();
                        return;
                    }
                    const newTrailer = getTrailerDeviceEntity(tempTrailer.name, tempTrailer.comment);
                    return getTrailerGroups(this.api, currentApiUser?.companyGroups ?? [])
                        .then(groups => {
                            newTrailer.groups = groups;
                            return addAsset(this.api, newTrailer, "Device");
                        })
                        .then(assetId => {
                            // Re-read the created asset so trailerFromDevice can key it for the active identity
                            return getAsset(this.api, { id: assetId }, "Device")
                                .then(devices => {
                                    if (devices && devices.length) {
                                        updateTrailerAttachment(this.trailerFromDevice(devices[0]));
                                    } else {
                                        // Rare corner case scenario, so log the error and reject
                                        reject(new Error("Device not found in the database"));
                                    }
                                });
                        });
                }).catch(reject);
        });
    }

    handleOfflineTrailerAttachments(
        localAttachments: IIdDictionary<TrailerAttachment>,
        postSyncCallback?: (error?: IApiError, skipTrailerFetchAndResolve?: boolean) => void
    ): Promise<IIdDictionary<TrailerAttachment>> {
        const currentActiveUserId = this.userService.getCurrentUser()?.id;
        const allTrailers = this.trailerCache.getDict(this.currentUser?.id);
        const attachmentPromises = Object.keys(localAttachments)
        .reduce((promises, attachmentId) => {
            const attachment = localAttachments[attachmentId];
            if (!attachment.modified) {
                return promises;
            }
            if (!attachment.trailer || !attachment.trailer.id) {
                logger.error(logger.Source.SYNC, `Missing trailer or trailer.id for local attachment ${ attachment.id }`, this.currentUser);
                return promises;
            }
            const tempTrailer = allTrailers[attachment.trailer.id];
            if (!tempTrailer) {
                // Log if the trailer is not found in the current apiUser cache
                logger.error(logger.Source.SYNC, `Missing trailer ${ attachment.trailer.id } in the cache. Local: ${ attachment.id }`, this.currentUser);
                return promises;
            }

            if (tempTrailer._isTemp) {
                promises.push(this.processOfflineAttachment(attachment, new Trailer(tempTrailer), currentActiveUserId));
            }
            return promises;
        }, [] as Promise<void>[]);

        return Promise.all(attachmentPromises).then(() => localAttachments).catch(error => {
            postSyncCallback && postSyncCallback(error, true);
            return Promise.reject(error);
        });
    }

    syncAttachments(
        serverAttachments: ITrailerAttachment[],
        postSyncCallback?: (error?: IApiError, skipTrailerFetchAndResolve?: boolean) => void
    ): void {
        const currentApiUser = this.currentUser;
        let attachmentsFromLocalStorage = this.getAttachments() || {};
        this.reconcileTrailerIdentity(attachmentsFromLocalStorage)
        .then(attachments => this.handleOfflineTrailerAttachments(attachments, postSyncCallback))
        .then(validLocalAttachments => {
            let localAttachments = this.resolveConflict(serverAttachments, validLocalAttachments);
            // To prevent creating duplicate trailers
            // Do not allow non-active user to add trailerAttachment in case there is a offline attachment
            if (currentApiUser?.id !== this.userService.getCurrentUser()?.id) {
                this.setAttachments(localAttachments);
                postSyncCallback && postSyncCallback(undefined, true);
                return;
            }

            this.pushChangedToServer(localAttachments, postSyncCallback);
        });
    }

    /**
     * MYG-157137: an unsynced attachment keeps the trailer id it was created with, which may belong to
     * the identity of the other side of DRIVE.ENABLE_TRAILER_DEVICE_LINK_API.RELEASE. Sent as is, the
     * server rejects it and the attachment is dropped.
     */
    reconcileTrailerIdentity(localAttachments: IIdDictionary<TrailerAttachment>): Promise<IIdDictionary<TrailerAttachment>> {
        const unsyncedIds = Object.keys(localAttachments).filter(id => localAttachments[id].modified && localAttachments[id].trailer?.id);
        if (!unsyncedIds.length) {
            return Promise.resolve(localAttachments);
        }
        return this.trailerIdentityReconciler
            .reconcile(unsyncedIds.map(id => localAttachments[id].trailer.id))
            .then(translations => {
                unsyncedIds.forEach(id => {
                    const attachment = localAttachments[id];
                    const translatedId = translations[attachment.trailer.id];
                    translatedId && (attachment.trailer = { ...attachment.trailer, id: translatedId });
                });
                return localAttachments;
            });
    }

    resolveConflict(
        serverAttachments: ITrailerAttachment[],
        localAttachments: IIdDictionary<TrailerAttachment>
    ): IIdDictionary<TrailerAttachment> {
        // Merge in server attachments with local attachments
        serverAttachments.forEach(attachment => {
            const server = new TrailerAttachment(attachment);
            let local = localAttachments[attachment.id];
            let previousState: TrailerAttachment;
            let modifiedState: IModifiedType | undefined;

            // Server attachment exists locally
            if (local) {
                // Conflict resolution
                local = new TrailerAttachment(local);
                previousState = new TrailerAttachment(local.previousState);
                modifiedState = local.modified;

                /*
                 Four scenarios:
                 local changed, server same => take local
                 local changed, server changed => conflict if local was "edit", merge local and server if local was "add"
                 local same, server changed => take server
                 local same, server same => do nothing
                 */

                if (modifiedState) {
                    //Local changed
                    if (!isEqual(previousState.toJSON(), server.toJSON())) {
                        if (modifiedState === "Add") {
                            // server has a different version, so merge both
                            logger.log(logger.Source.SYNC, "Merged local TrailerAttachment with server", this.currentUser);
                            localAttachments[attachment.id] = new TrailerAttachment(common.merge<TrailerAttachment>(localAttachments[attachment.id], server.toJSON(), { modified: "Set" }));
                        } else {
                            logger.error(logger.Source.SYNC, `SYNC: TrailerAttachment conflict (both server and client changed). LocalID: ${ local.id }, ServerID: ${ server.id }`, this.currentUser);
                        }
                    }
                } else {
                    // local same
                    if (!isEqual(local.toJSON(), server.toJSON())) {
                        logger.log(logger.Source.SYNC, "Updated local TrailerAttachment from server", this.currentUser);
                        localAttachments[attachment.id] = server;
                    }
                }
            } else {
                localAttachments[attachment.id] = server;
            }
        });
        return localAttachments;
    }

    prepareLocalAttachmentsForSync(localAttachments: IIdDictionary<TrailerAttachment>): [IModifiedType, ISyncAddSet<ITrailerAttachment>][] {
        const calls: [IModifiedType, ISyncAddSet<ITrailerAttachment>][] = [];
        Object.keys(localAttachments).forEach((attachmentId: string) => {
            const attachment = localAttachments[attachmentId];

            if (attachment.id === "") {
                throw new Error("Attempting to sync a record without an ID");
            }

            if (attachment.modified) {
                const sanitizedLog: ITrailerAttachment = extend({}, attachment);
                this.removeClientSideProperties(sanitizedLog);

                if (attachment.modified === "Add" || attachment.modified === "Set") {
                    if (attachment._syncCounter === undefined) {
                        attachment._syncCounter = NumberOfSyncRetries;
                    }
                    if (attachment._syncCounter > 0) {
                        if (attachment.hasNoTrailerId() || attachment.hasNoDeviceId()) {
                            // delete invalid attachment if it has no deviceId or trailerId.
                            delete localAttachments[attachment.id];
                        } else {
                            attachment._syncCounter--;
                            if (this.usesDeviceLinkApi()) {
                                // Wire shape only: the pushed "entity" is a DeviceLink, not a TrailerAttachment.
                                // resolveSyncedEntities only reads entity.id, which is preserved by toDeviceLinkEntity,
                                // so the ISyncAddSet<ITrailerAttachment> typing downstream stays safe to use.
                                calls.push([attachment.modified, { typeName: "DeviceLink", entity: toDeviceLinkEntity(sanitizedLog) as unknown as ITrailerAttachment }]);
                            } else {
                                calls.push([attachment.modified, { typeName: "TrailerAttachment", entity: sanitizedLog }]);
                            }
                        }
                    }
                }
            }
        });
        // Update localStorage
        this.setAttachments(localAttachments);
        return calls;
    }

    fetchData(postSyncCallback?: (error?: IApiError, skipTrailerFetchAndResolve?: boolean) => void): void {
        const currentVehicle = getCurrentVehicle();
        const attachments = this.getAttachments() || {};
        const feedVersion = common.getFeedVersion(this.api, this.store, FeedTypeKey.TRAILER_ATTACHMENT);
        if (currentVehicle && currentVehicle.id !== "NoDeviceId") {
            // The server does support GetFeed:DeviceLink (DeviceLink declares GetFeed rate and timeout
            // limits, and DeviceLinkBridge overrides GetFeedAsync). This path still takes the full
            // "Get" as a deliberate first-iteration simplification: the DeviceLink feed is a separate
            // version space, so using it needs its own FeedTypeKey rather than the TrailerAttachment
            // key, and GetFeedAsync force-nulls ActiveTo. Tracked as follow-up work, not a limitation.
            if (!this.usesDeviceLinkApi() && feedVersion && attachments && Object.keys(attachments).length > 0) {
                this.fetchAttachmentsUsingGetFeed(attachments, currentVehicle, feedVersion, postSyncCallback);
            } else {
                this.fetchAttachmentsUsingGet(currentVehicle, postSyncCallback);
            }
        } else {
            this.syncAttachments([], postSyncCallback);
        }
    }

    synchronize(profilingBatcher?: PerformanceProfileBatcher, forceRefresh = false) {
        return this.createProfiledSync(MeasureName.SyncTrailerAttachment, () => {
            return new Promise(resolve =>
                this.fetchData((fetchAttachmentsError, skipTrailerFetchAndResolve) => {
                    if (skipTrailerFetchAndResolve) {
                        fetchAttachmentsError && handleApi(fetchAttachmentsError);
                        resolve(["TrailerAttachments", fetchAttachmentsError]);
                        return;
                    }
                    this.fetchAttachmentsTrailers(forceRefresh)
                        .then(fetchTrailersError => syncDefectLists(this.securityService, this.api, this.store).then(() => fetchTrailersError))
                        .then(fetchTrailersError => {
                            // If no errors happened then trigger sync:trailerAttachments event
                            if (!fetchAttachmentsError && !fetchTrailersError) {
                                dispatcher.trigger(DispatcherEvents.SyncTrailerAttachments);
                                // Push any attachments marked as modified during cacheAsTrailer
                                // (e.g. inaccessible attachments expired with modified="Set").
                                // Thread clear() and resolve() through the callback so
                                // handleSuccessfulSync always completes before the sync resolves.
                                // Calling pushChangedToServer without a callback lets
                                // handleSuccessfulSync fire after resolve(), creating a
                                // stale-snapshot window that can silently overwrite concurrent
                                // store writes.
                                this.pushChangedToServer(this.getAttachments(), () => {
                                    this.clear();
                                    resolve(["TrailerAttachments", null]);
                                });
                            } else {
                                resolve(["TrailerAttachments", fetchAttachmentsError || fetchTrailersError]);
                            }
                        })
                        .catch(error => {
                            error && handleApi(error);
                            resolve(["TrailerAttachments", error]);
                        });
                }));
        }, profilingBatcher);
    }

    /**
     * Will throw an error causing caller to reject promise
     * @param obj TrailerAttachment object
     */
    checkValue(obj: ITrailerAttachment): void {
        if (!obj.activeFrom) {
            throw new Error("Must have an activeFrom date");
        }

        if (!obj.activeTo) {
            throw new Error("Must have an activeTo date");
        }

        if (new Date(obj.activeFrom) > new Date(obj.activeTo)) {
            throw new Error("activeFrom must be older than activeTo");
        }

        if (!obj.device) {
            throw new Error("Must specify a device");
        }

        if (!obj.trailer || !obj.trailer.id || common.noEntityIds.indexOf(obj.trailer.id) !== -1) {
            throw new Error("Must specify a trailer");
        }
    }

    /**
     * Invalidates the non-_isTemp trailer cache entries for this instance's user so
     * the next sync performs a fresh server round-trip. Called by Sync.onSelectNewVehicle
     * via applyActionToAllContexts, which already handles multi-driver correctly.
     * _isTemp trailers belong to unsynced offline attachments and are preserved.
     * Gated by DRIVE.AUTO_EXPIRE_INACCESSIBLE_TRAILER_ATTACHMENT.KILLSWITCH.
     */
    invalidateTrailerCache(): void {
        if (this.featureFlagsService.hasFeatureFlag(FeatureFlagName.AUTO_EXPIRE_INACCESSIBLE_TRAILER_ATTACHMENT)) {
            return;
        }
        const userId = this.currentUser?.id;
        if (!userId) {
            return;
        }
        const cachedTrailers = this.trailerCache.getDict(userId);
        // Read directly from the store without cloning — we only need trailer IDs.
        const storedAttachments = this.store.getState().trailerAttachments[userId];
        if (!storedAttachments) {
            return;
        }
        Object.keys(storedAttachments).forEach(attachmentId => {
            const trailerId = storedAttachments[attachmentId]?.trailer?.id;
            if (!trailerId) {
                logger.log(logger.Source.SYNC, `Attachment ${ attachmentId } has no trailer id — skipping cache invalidation`, this.currentUser);
                return;
            }
            const cached = cachedTrailers[trailerId];
            if (cached && !cached._isTemp) {
                this.trailerCache.remove({ id: trailerId }, userId);
            }
        });
    }

    /**
     * MYG-148762: when on, trailer attach/detach sync reads and writes the
     * server-side DeviceLink API instead of TrailerAttachment. The local
     * TrailerAttachment domain model and storage format are unchanged —
     * translation happens only at this API boundary (see
     * ./trailerAttachmentDeviceLink) — so no local migration is required and
     * every existing UI call site keeps working unmodified.
     */
    private usesDeviceLinkApi(): boolean {
        return usesTrailerDeviceIdentity(this.store);
    }

    private trailerFromDevice(device: IDevice): Trailer {
        return new Trailer({
            ...device,
            id: this.usesDeviceLinkApi() ? device.id : device.tmpTrailerId
        });
    }

    private handleSuccessfulSync(
        calls: [IModifiedType, ISyncAddSet<ITrailerAttachment>][],
        results: string[],
        localAttachments: IIdDictionary<TrailerAttachment>,
        postSyncCallback?: (error?: IApiError, skipTrailerFetchAndResolve?: boolean) => void
    ): void {
        const getLocalEntity = (entity: ITrailerAttachment): TrailerAttachment => localAttachments[entity.id];
        this.resolveSyncedEntities<ITrailerAttachment>(calls, results, getLocalEntity);

        // Update localStorage
        this.setAttachments(localAttachments);
        postSyncCallback?.();
    }

    private pushChangedToServer(
        localAttachments: IIdDictionary<TrailerAttachment>,
        postSyncCallback?: (error?: IApiError, skipTrailerFetchAndResolve?: boolean) => void
    ): void {
        const calls: [IModifiedType, ISyncAddSet<ITrailerAttachment>][] = this.prepareLocalAttachmentsForSync(localAttachments);

        if (calls.length > 0) {
            logger.log(logger.Source.SYNC, common.formatCallForLogger("TrailerAttachments", calls), this.currentUser);
            this.api.multiCall(
                calls,
                (results) => this.handleSuccessfulSync(calls, results, localAttachments, postSyncCallback),
                (errorObject) => this.handleSyncError(errorObject, calls, localAttachments, postSyncCallback)
            );
        } else {
            this.setAttachments(localAttachments);
            postSyncCallback?.();
        }
    }

    private handleSyncError(
        errorObject: IApiError,
        calls: [IModifiedType, ISyncAddSet<ITrailerAttachment>][],
        localAttachments: IIdDictionary<TrailerAttachment>,
        postSyncCallback?: (error?: IApiError, skipTrailerFetchAndResolve?: boolean) => void
    ): void {
        // Handle any successfully completed calls so they are not re-synced
        if ((errorObject.data?.requestIndex ?? 0) > 0) {
            this.handlePartialMultiCallSuccess(errorObject, calls, (entity: ITrailerAttachment) => localAttachments[entity.id]);
            this.setAttachments(localAttachments);
        }

        if (isOverLimitException(errorObject)) {
            postSyncCallback?.(errorObject);
            return;
        }

        const requestIndex = errorObject.data?.requestIndex;
        const call = requestIndex !== undefined ? calls[requestIndex] : undefined;
        const log = call?.[1].entity;
        const hasAttachmentLocally = !!log && localAttachments.hasOwnProperty(log.id);

        if (isDuplicateException(errorObject) && hasAttachmentLocally) {
            // This attachment is already in the database, make it a "Set" and sync again
            localAttachments[log.id].modified = "Set";
            this.setAttachments(localAttachments);
            this.pushChangedToServer(localAttachments, postSyncCallback);
            return;
        }

        if (isRelationViolatedException(errorObject) && hasAttachmentLocally) {
            this.handleTrailerRemovedError(localAttachments, log, postSyncCallback);
            return;
        }

        if (isEntityRemoved(errorObject)) {
            if (hasAttachmentLocally) {
                delete localAttachments[log.id];
            }
            this.setAttachments(localAttachments);
            this.pushChangedToServer(localAttachments, postSyncCallback);
            return;
        }

        if (isOverlappingTrailerAttachmentException(errorObject) && hasAttachmentLocally) {
            logger.error(logger.Source.SYNC, `TrailerAttachment ArgumentException — removing attachment ${ log.id } from local storage: ${ errorObject.message }`, this.currentUser);
            delete localAttachments[log.id];
            this.setAttachments(localAttachments);
            this.pushChangedToServer(localAttachments, postSyncCallback);
            return;
        }

        dispatcher.trigger(DispatcherEvents.SyncTrailerAttachments);
        postSyncCallback?.(errorObject);
        handleApi(errorObject);
    }

    private handleTrailerRemovedError(
        localAttachments: IIdDictionary<TrailerAttachment>,
        log: ITrailerAttachment,
        postSyncCallback?: (error?: IApiError, skipTrailerFetchAndResolve?: boolean) => void
    ): void {
        // Trailer was deleted
        delete localAttachments[log.id];
        const noTrailerDialog = new Dialog(
            "trailer-removed-dialog",
            "The trailer you are attached to no longer exists on the server.<br><br>Please select another trailer to attach.",
            this.translator,
            "Trailer Removed",
            [{
                text: "OK",
                id: "trailer-removed-ok",
                callback: () => {
                    noTrailerDialog.remove();
                }
            }],
            {
                isNightMode: this.store.getState().data.nightMode
            }
        );
        void noTrailerDialog.open();
        this.setAttachments(localAttachments);
        this.pushChangedToServer(localAttachments, postSyncCallback);
    }

    private fetchAttachmentsUsingGetFeed(
        attachments: IIdDictionary<TrailerAttachment>,
        currentVehicle: IIdEntity,
        feedVersion: string,
        postSyncCallback?: (error?: IApiError, skipTrailerFetchAndResolve?: boolean) => void
    ): void {
        // Find the minimum activeFrom date from all attachments
        const minActiveFrom = Object.keys(attachments).reduce((minDate, attachmentId) => {
            const attachment = attachments[attachmentId];
            if (attachment.activeFrom) {
                const activeFromDate = new Date(attachment.activeFrom);
                return !minDate || activeFromDate < minDate ? activeFromDate : minDate;
            }
            return minDate;
        }, null as Date | null);

        const trailerAttachmentSearch = {
            deviceSearch: { id: currentVehicle.id },
            activeFrom: minActiveFrom ? minActiveFrom.toISOString() : getServerDate().toISOString()
        };
        this.api.call("GetFeed", {
            typeName: "TrailerAttachment",
            fromVersion: feedVersion,
            search: trailerAttachmentSearch
        }, result => {
            const feedAttachments = result.data || [];
            const version = result.toVersion;

            logger.log(logger.Source.SYNC, `Downloaded ${ feedAttachments.length } trailer attachments`, this.currentUser);

            this.syncAttachments(feedAttachments, postSyncCallback);
            common.setFeedVersion(this.api, this.store, FeedTypeKey.TRAILER_ATTACHMENT, version);
        }, errorObject => {
            postSyncCallback?.(errorObject);
            handleApi(errorObject);
        });
    }

    private fetchAttachmentsUsingGet(
        currentVehicle: IIdEntity,
        postSyncCallback?: (error?: IApiError, skipTrailerFetchAndResolve?: boolean) => void
    ): void {
        // One builder for both identities, so the vehicle→side mapping lives in exactly one place.
        // The decoder is pinned to the branch that built the call rather than re-reading the flag.
        const { call: [method, params], decode } = toTrailerAttachmentRead({
            deviceSearch: { id: currentVehicle.id },
            activeFrom: getServerDate().toISOString()
        });

        this.api.call(
            method,
            params,
            (data: unknown[]) => {
                const attachments = decode(data);
                logger.log(logger.Source.SYNC, `Downloaded ${ attachments.length } trailer attachments`, this.currentUser);
                this.syncAttachments(attachments, postSyncCallback);

                if (this.usesDeviceLinkApi()) {
                    // A DeviceLink version belongs to a different version space than TrailerAttachment.
                    // Storing one under the TrailerAttachment key would make a later flag-off session
                    // resume GetFeed from a foreign version and skip or replay attachments, so any
                    // version left by an earlier session is cleared instead.
                    common.clearFeedVersion(this.api, this.store, FeedTypeKey.TRAILER_ATTACHMENT);
                    return;
                }
                common.setFeedVersion(this.api, this.store, FeedTypeKey.TRAILER_ATTACHMENT, this.getLatestVersion(attachments));
            },
            errorObject => {
                postSyncCallback?.(errorObject);
                handleApi(errorObject);
            }
        );
    }

    private fetchAttachmentsTrailers(forceRefresh = false): Promise<IApiError | null> {
        const attachments = this.getAttachments();
        const apiUserId = this.currentUser?.id;
        let syncError: IApiError | null = null;
        return this.cacheAsTrailer(attachments, apiUserId, forceRefresh)
            .then((error: IApiError | null) => {
                syncError = error;
                if (error === null && this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_CACHE_TRAILER_AS_DEVICE)) {
                    return this.cacheAsDevice(attachments, apiUserId);
                }
                return Promise.resolve(error);
            })
            .then(error => Promise.resolve(syncError || error));
    }

    private cacheAsTrailer(attachments: IIdDictionary<TrailerAttachment>, apiUserId?: string, forceRefresh = false): Promise<IApiError | null> {
        const trailerIds = Object.keys(attachments).reduce((ids: string[], attachmentId) => {
            const attachment = attachments[attachmentId];
            const trailer = attachment?.trailer;
            trailer && ids.indexOf(trailer.id) === -1 && ids.push(trailer.id);
            return ids;
        }, []);

        if (trailerIds.length) {
            const killswitchOn = this.featureFlagsService.hasFeatureFlag(FeatureFlagName.AUTO_EXPIRE_INACCESSIBLE_TRAILER_ATTACHMENT);

            // On a forced refresh ("Check for updates"), invalidate non-_isTemp cache
            // entries so getOrFetch makes a fresh server call and detects permission
            // changes. On regular syncs the cache is kept; invalidateTrailerCache()
            // (called from Sync.onSelectNewVehicle) handles the vehicle-switch path.
            if (forceRefresh && !killswitchOn) {
                trailerIds.forEach(id => {
                    if (!this.trailerCache.get({ id }, apiUserId)[0]?._isTemp) {
                        this.trailerCache.remove({ id }, apiUserId);
                    }
                });
            }

            return this.trailerCache
                .getOrFetch(this.api, trailerIds.map(id => ({ id })), apiUserId)
                .then(trailers => {
                    const allTrailers: ITrailer[] = [].concat(...trailers);
                    const availableTrailerIds = allTrailers.map(trailer => trailer.id);
                    // If some trailers are not accessible, remove their attachments from local
                    // state and expire them on the server so HOS logs stay consistent.
                    const availableAttachments = Object.keys(attachments).reduce((res: IIdDictionary<TrailerAttachment>, attachmentId) => {
                        const attachment = attachments[attachmentId];
                        const attachedTrailer = attachment.trailer;
                        if (attachedTrailer && availableTrailerIds.indexOf(attachedTrailer.id) > -1) {
                            res[attachmentId] = attachment;
                        } else if (!killswitchOn) {
                            logger.log(
                                logger.Source.SYNC,
                                `Detaching TrailerAttachment ${ attachmentId } — trailer ${ attachedTrailer?.id } is no longer accessible to the driver`,
                                this.currentUser
                            );
                            // If activeTo is already in the past the attachment was already expired
                            // by a previous sync (pushChangedToServer succeeded). Just remove it
                            // from local state — no need to mark again, which would create a loop.
                            const isAlreadyExpired = !!attachment.activeTo && new Date(attachment.activeTo).getTime() <= getServerDate().getTime();
                            if (!isAlreadyExpired) {
                                // Mark as modified = "Set" with activeTo = now so pushChangedToServer
                                // expires it on the server via the standard sync mechanism with retries.
                                const expiredAttachment = new TrailerAttachment(extend({}, attachment));
                                expiredAttachment.activeTo = getServerDate().toISOString();
                                expiredAttachment.modified = "Set";
                                res[attachmentId] = expiredAttachment;
                            }
                            // else: activeTo is in the past — exclude from availableAttachments,
                            // which removes the attachment from local state cleanly.
                        }
                        return res;
                    }, <any>{});
                    this.setAttachments(availableAttachments);
                    return Promise.resolve(null);
                })
                .catch((error) => Promise.resolve(error));
        } else {
            return Promise.resolve(null);
        }
    }

    private cacheAsDevice(attachments: IIdDictionary<TrailerAttachment>, apiUserId?: string): Promise<IApiError | null> {
        if (this.usesDeviceLinkApi()) {
            // The trailer is already cached as its own Device by cacheAsTrailer under this flag.
            return Promise.resolve(null);
        }

        const trailerNames = Object.keys(attachments).reduce((names: string[], attachmentId) => {
            const attachment = attachments[attachmentId];
            if (!attachment.trailer) {
                return names;
            }
            const trailer = this.trailerCache.get(attachment.trailer, apiUserId)[0];
            trailer && names.indexOf(trailer.name) === -1 && names.push(trailer.name);
            return names;
        }, []);

        if (trailerNames.length) {
            return this.trailerDeviceCache
                .fetch(this.api, trailerNames.map(name => ({ name, groups: [{ id: "GroupTrailerId" }], fromDate: getServerDate().toISOString() })), true, apiUserId, 1)
                .then(() => {
                    return Promise.resolve(null);
                })
                .catch((error) => Promise.resolve(error));
        } else {
            return Promise.resolve(null);
        }
    }
}